import { z } from "zod";
//#region src/surfaces/surface-type-language.ts
/**
* The closed type language a surface's `input`, `outputs`, `uses[]` shapes and `settings` are
* written in (layered shell surfaces, D8, R-20, R-35).
*
* The language is JSON's six types (string, number, boolean, null, object, array) plus a string
* enum, optional fields, a discriminated union and one opaque `unknown`. A free string carries a
* `maxLength` of at most {@link SURFACE_STRING_MAX_LENGTH} and may carry a `pattern`, which is a
* name from {@link SURFACE_PATTERNS} and never a regular expression (R-36, R-54). An enum or a tag
* may carry a `maxLength` too, and every value it lists must fit it (R-52). Every length in the language counts
* Unicode code points, the unit JSON Schema's `maxLength` and Ajv count. A schema crosses the
* wire as JSON Schema draft 2020-12, in exactly the forms zod 4's `z.toJSONSchema` emits for those
* constructs with `io: 'input'`. An object is always closed (`additionalProperties: false`),
* because only a closed model makes "a field was added" checkable (`research/14` §1).
*
* Three readers judge the same language:
* - {@link isRepresentable} here, the boolean verdict over a JSON Schema node;
* - WS-1's `firstUnrepresentableNode`, which names the first failing node and must agree with
*   {@link isRepresentable} on every input;
* - `surface-type.schema.json`, the Ajv meta-schema the publish door compiles. It admits every
*   representable schema; four rules it cannot state (the depth and node caps, `$schema` below
*   the root and `const` outside a discriminator, `required` naming an undeclared property, an
*   enum member or tag longer than its own `maxLength`) are the rule modules' to refuse. It
*   states the `pattern` names itself, as an `enum` (R-54).
*
* This module does no I/O and holds no state.
*/
/** The one `$schema` value a declared schema may carry, and only at its root. */
const SURFACE_JSON_SCHEMA_DIALECT = "https://json-schema.org/draft/2020-12/schema";
/**
* The deepest schema node the language admits. The root node is depth 1 and each `properties`
* value, `items` and `oneOf` variant is one deeper, so a depth-17 node is refused. WS-1 imports
* this constant; it does not declare its own (R-20 Q15, escalated in the S1 contracts report).
*/
const SURFACE_SCHEMA_MAX_DEPTH = 16;
/**
* The most nodes one schema may hold. Every schema node visited counts one and every `enum`
* member counts one; the walk stops at the 513th.
*/
const SURFACE_SCHEMA_MAX_NODES = 512;
/**
* The largest `maxLength` a free string node may declare, and the bound every free string must
* declare (R-36). A schema that crosses to a tenant's console form, a DP row or a mounted surface
* never admits an unbounded string. `pattern` narrows a string and never stands in for the bound.
*/
const SURFACE_STRING_MAX_LENGTH = 4096;
/**
* The closed table of string patterns a surface schema may name (R-54). A string node writes
* `pattern: 'hex-color'`, never a regular expression, so no publisher's regex ever runs on the CP,
* on the DP or in a browser. Each value is the regular-expression source every reader compiles
* under the `u` flag and tests as a whole-value match (each source is anchored with `^` and `$`):
* - `hex-color`: `#` and six hex digits, either case; the form `OrgBrandingSchema` takes;
* - `slug`: lower-case letters, digits and inner hyphens, two characters or more; the source of
*   `RESOURCE_SLUG_PATTERN`;
* - `uuid`: the 8-4-4-4-12 hex form, either case;
* - `url`: `https://`, a host of ASCII letters, digits, hyphens and dots, an optional port, then an
*   optional path, query or fragment of printable ASCII with no space. The shape only: no user
*   info, no `http:`. A reader that fetches the value still checks its host against an allowlist;
* - `email`: an ASCII local part of letters, digits and `._%+-`, `@`, then two or more domain
*   labels of letters, digits and hyphens;
* - `iso-date`: `YYYY-MM-DD` with a month from 01 to 12 and a day from 01 to 31; the calendar is
*   not checked, so `2026-02-31` matches;
* - `semver`: `MAJOR.MINOR.PATCH` with no leading zero, an optional `-` pre-release and an
*   optional `+` build, each of dot-separated `[0-9A-Za-z-]` identifiers.
*
* Every class is an allowlist and every source is unambiguous: each quantified part is followed by
* a character it cannot hold, so a test costs time linear in the value (S1 rejudge-8). The readers
* are the zod builder ({@link surfaceJsonSchema}), {@link isRepresentable}, the walk, the Ajv
* meta-schema (as an `enum` of the keys), the cp-dp twin, `findAssignabilityMismatch`,
* {@link surfaceValidatorSchema}, which writes a row's source for a stock validator, and
* `@zion/sdk-surfaces`' `validateSurfaceValue`, which holds a copy its test compares. Frozen;
* adding a row is a contract change for all of them.
*/
const SURFACE_PATTERNS = Object.freeze({
	"hex-color": "^#[0-9a-fA-F]{6}$",
	slug: "^[a-z0-9][a-z0-9-]*[a-z0-9]$",
	uuid: "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
	url: "^https://[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)*(?::[0-9]{1,5})?(?:[/?#][!-~]*)?$",
	email: "^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)+$",
	"iso-date": "^[0-9]{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$",
	semver: "^(?:0|[1-9][0-9]*)\\.(?:0|[1-9][0-9]*)\\.(?:0|[1-9][0-9]*)(?:-[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?(?:\\+[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?$"
});
/** Each table row compiled once under `u`. Module-private: no caller can reach a `RegExp` to mutate it. */
const COMPILED_SURFACE_PATTERNS = new Map(Object.entries(SURFACE_PATTERNS).map(([name, source]) => [name, new RegExp(source, "u")]));
/**
* True when `value` is an own key of {@link SURFACE_PATTERNS}. `value` is untrusted; an inherited
* name such as `constructor` or `__proto__` gives `false`. Module-internal: the package barrel does
* not export it. Pure; never throws.
*/
function isSurfacePatternName(value) {
	return typeof value === "string" && Object.hasOwn(SURFACE_PATTERNS, value);
}
/**
* True when `value` matches the table row `name` as a whole value, the test every reader runs.
* Costs time linear in `value`. Module-internal. Pure; never throws.
*/
function matchesSurfacePattern(name, value) {
	return COMPILED_SURFACE_PATTERNS.get(name).test(value);
}
/** The two annotation keywords any node may carry. Neither is walked or type-checked beyond `description` being a string. */
const SURFACE_TYPE_ANNOTATIONS = Object.freeze(["default", "description"]);
/**
* True when `value` holds more than `max` Unicode code points, the unit every length in the type
* language counts (R-52). A string holds at least `length / 2` and at most `length` code points,
* so only a length between `max` and `2 * max` is counted, and the count stops at `max + 1`.
*
* Shared by the four readers in this package: the builder's `.max`, {@link isRepresentable},
* `firstUnrepresentableNode` and `findAssignabilityMismatch` (S1 rejudge-6). Module-internal: the
* package barrel does not export it. Pure; never throws.
*/
function exceedsCodePoints(value, max) {
	if (value.length <= max) return false;
	if (value.length > 2 * max) return true;
	let count = 0;
	for (const _codePoint of value) {
		count += 1;
		if (count > max) return true;
	}
	return false;
}
/**
* zod's `$ZodCheckMaxLength`, counting code points. It keeps the parent's `onattach`, so
* `bag.maximum` and the JSON Schema export are zod's own. zod's `instanceof` reads the trait set,
* so an instance is both a `$ZodCheckMaxLength` and a `SurfaceCheckMaxCodePoints`.
*/
const SurfaceCheckMaxCodePoints = z.core.$constructor("$SurfaceCheckMaxCodePoints", (inst, def) => {
	z.core.$ZodCheckMaxLength.init(inst, def);
	inst._zod.check = (payload) => {
		const input = payload.value;
		if (!exceedsCodePoints(input, def.maximum)) return;
		payload.issues.push({
			origin: "string",
			code: "too_big",
			maximum: def.maximum,
			inclusive: true,
			input,
			inst,
			continue: !def.abort
		});
	};
});
/**
* `ZodString` whose `.max` installs {@link SurfaceCheckMaxCodePoints}. zod clones a schema through
* its constructor on every chained call, so the override survives `.regex()`, `.optional()` and
* `.describe()`.
*/
const SurfaceString = z.core.$constructor("SurfaceString", (inst, def) => {
	z.ZodString.init(inst, def);
	inst.max = (maximum, params) => inst.check(new SurfaceCheckMaxCodePoints({
		check: "max_length",
		...z.core.util.normalizeParams(params),
		maximum
	}));
});
/** The `string` builder: `z.string(params)` with a code-point `.max`. */
function surfaceString(params) {
	return z.core._string(SurfaceString, params);
}
/**
* The builders an author writes a schema with. Each is the zod 4 function of the same meaning;
* `object` is `z.strictObject`, because `z.object` exports an open object under `io: 'input'`
* (measured on zod 4.4.3: no `additionalProperties` member). `union` is `z.discriminatedUnion`
* over `object` variants whose discriminator is a `tag`. Optional fields use zod's own
* `.optional()` and `.default(value)`; annotations use `.describe(text)`.
*
* A free string must be bounded: write `string().max(n)` with `n` at most
* {@link SURFACE_STRING_MAX_LENGTH} (R-36). To narrow it, add one
* `.regex(new RegExp(SURFACE_PATTERNS['hex-color'], 'u'))`: a table row's source with the `u` flag
* and no other. {@link surfaceJsonSchema} exports that check as `pattern: 'hex-color'` (R-54); any
* other regex stays outside the language. A builder result can still be chained into a construct
* outside the language (a bare `string()`, `.min()`, a second `.regex()`, `.int()`, `.refine()`,
* `.trim()`). {@link surfaceJsonSchema} refuses those at export, so a schema that reaches
* `app.json` through it is representable.
*
* `string` is zod's `ZodString` with one difference: its `.max(n)` installs the package's own
* code-point check (R-52). zod 4.6's stock `.max(n)` counts code points too, but
* {@link surfaceJsonSchema} admits only this form. The export still writes `maxLength: n`, and
* JSON Schema, Ajv, `findAssignabilityMismatch` and `@zion/sdk-surfaces`' `validateSurfaceValue`
* count code points too. So a value the publish door proves a reader accepts is one the reader's
* own parser accepts: `string().max(4)` admits three emoji (6 code units) and refuses five
* (S1 architecture-5). The check reports zod's `too_big` issue with `origin: 'string'`.
* Every other `ZodString` method, and every chained clone, behaves as zod's.
*/
const surfaceTypes = Object.freeze({
	string: surfaceString,
	number: z.number,
	boolean: z.boolean,
	null: z.null,
	unknown: z.unknown,
	enum: z.enum,
	tag: z.literal,
	array: z.array,
	object: z.strictObject,
	union: z.discriminatedUnion
});
/**
* Thrown by {@link surfaceJsonSchema}. `not-exportable`: zod could not export the schema at all
* (a `Date`, a bare `z.transform()`, a custom type); the message carries zod's reason. `not-representable`:
* zod exported it, and the export leaves the closed language (an `integer`, an unbounded string, a
* `minLength`, an open object, a plain union, a regex, `.includes()`, `.startsWith()` or string
* format that is not one {@link SURFACE_PATTERNS} row),
* or the schema parses differently from its export: a node is a `.pipe()`, `.transform()` or
* `z.preprocess()`, whose export is one side of the pipe (S1 rejudge-3); a node carries a custom
* check (`.refine()`, `.superRefine()`, `.check(fn)`), whose predicate the export drops
* (S1 architecture-8); a node carries an overwrite check (`.trim()`, `.toLowerCase()`,
* `.toUpperCase()`, `.normalize()`, `.overwrite(fn)`), which changes the value the parser hands
* on (R-55, E5); or a string bounds itself with zod's own `.max(n)` in place of the
* {@link surfaceTypes} bound.
*/
var SurfaceTypeLanguageError = class extends Error {
	name = "SurfaceTypeLanguageError";
	code;
	constructor(code, message) {
		super(message);
		this.code = code;
	}
};
/**
* Export a schema built with {@link surfaceTypes} as the JSON Schema `app.json` declares.
*
* Calls `z.toJSONSchema(schema, { io: 'input' })`, so a field with a default is optional, as D8
* requires of a field a consumer adds. Drops the root `$schema` member, the form WS-15 and WS-9
* pin against `app.json`. Returns a deep-frozen plain object, since a schema is immutable under its
* surface name.
*
* A string's one `.regex()` check whose `RegExp` has a {@link SURFACE_PATTERNS} row's source and
* the flags `u` exports as `pattern: <row name>` (R-54). Any other `pattern` zod writes on a string
* or template literal node is refused, even when its text equals a row name: `.regex(/slug/u)` and
* `.includes('slug')` both export as `pattern: 'slug'` and parse other values than the slug row
* (S1 architecture-10).
*
* The returned `pattern` is a table name, which a stock JSON Schema validator compiles as a regex
* and so misjudges every value. Hand a validator {@link surfaceValidatorSchema}'s form of the
* result, never the result itself (S1 security-8).
*
* Throws {@link SurfaceTypeLanguageError} with `not-exportable` when zod throws, and with
* `not-representable` in four cases, checked in this order:
* - any node is a `pipe` (`.pipe()`, `.transform()` on a schema, `z.preprocess()`), since the
*   export shows the input side and the parser also runs the output side (S1 rejudge-3); zod
*   itself refuses a bare `z.transform()`, which gives `not-exportable`;
* - any node carries a custom check (`.refine()`, `.superRefine()`, `.check(fn)` on any node,
*   string, number, object or other), since the export drops the predicate and the parser refuses
*   values the exported schema and the D21 relation admit (S1 architecture-8), or an overwrite
*   check (`.trim()`, `.toLowerCase()`, `.toUpperCase()`, `.normalize()`, `.overwrite(fn)`), since
*   the parser hands on a value other than the one the exported schema judged (R-55, E5);
* - any string node carries zod's stock `$ZodCheckMaxLength` (a `z.string().max(n)` written without
*   {@link surfaceTypes}). Before zod 4.6 that check counted UTF-16 code units and refused astral
*   text the exported `maxLength` admits (S1 architecture-5). zod 4.6 counts code points, and the
*   type language still admits only the {@link surfaceTypes} bound;
* - a string or template literal node carries a `pattern` that is not one table row as above, or
*   the export fails {@link isRepresentable}; the message names the string bound and the pattern
*   table.
* Pure apart from the throw.
*/
function surfaceJsonSchema(schema) {
	let exported;
	let unitCounted = false;
	let piped = false;
	let refined = false;
	let foreignPattern = false;
	try {
		exported = z.toJSONSchema(schema, {
			io: "input",
			override: (ctx) => {
				const def = ctx.zodSchema._zod.def;
				if (def.type === "pipe") piped = true;
				if (def.checks?.some(isCustomCheck)) refined = true;
				if (def.checks?.some(isUnitCountedMaxLength)) unitCounted = true;
				const name = tablePatternName(def.checks);
				if (name !== void 0) ctx.jsonSchema["pattern"] = name;
				else if (writesOwnPattern(def.type) && Object.hasOwn(ctx.jsonSchema, "pattern")) foreignPattern = true;
			}
		});
	} catch (error) {
		throw new SurfaceTypeLanguageError("not-exportable", `the schema cannot be exported as JSON Schema: ${error.message}`);
	}
	if (piped) throw new SurfaceTypeLanguageError("not-representable", "the schema pipes or transforms its value (.pipe(), .transform(), z.preprocess()), so its parser accepts other values than the exported schema states; declare the plain shape");
	if (refined) throw new SurfaceTypeLanguageError("not-representable", "the schema carries a custom check (.refine(), .superRefine(), .check(fn)) or an overwrite (.trim(), .toLowerCase(), .toUpperCase(), .normalize(), .overwrite(fn)), which JSON Schema cannot state, so its parser judges or hands on other values than the exported schema states; express the rule with the type language (an enum, a bounded string with one SURFACE_PATTERNS name) or drop it");
	if (unitCounted) throw new SurfaceTypeLanguageError("not-representable", "a string bounds itself with zod's own .max(n); the type language admits only surfaceTypes.string().max(n), the code-point bound that the exported maxLength states");
	const { $schema: _dialect, ...declared } = exported;
	if (foreignPattern || !isRepresentable(declared)) throw new SurfaceTypeLanguageError("not-representable", `the schema leaves the surface type language (D8): JSON types, string enum, optional, closed objects, arrays, a const-discriminated union and unknown only; every free string declares a maxLength of at most ${SURFACE_STRING_MAX_LENGTH} (R-36), and a pattern is a SURFACE_PATTERNS name (${Object.keys(SURFACE_PATTERNS).join(", ")}), written with .regex(new RegExp(SURFACE_PATTERNS[name], 'u')) (R-54)`);
	return deepFreeze(declared);
}
/**
* The form of a declared schema that a stock JSON Schema draft 2020-12 validator reads with the
* verdict the type language means (S1 security-8).
*
* A declared schema's `pattern` holds a {@link SURFACE_PATTERNS} name, never a regular expression
* (R-54). JSON Schema defines `pattern` as a regex, so a stock validator handed the declared form
* compiles `url` or `hex-color` as a regex: it refuses every value the parser accepts and accepts
* any value that merely contains the name, such as `javascript:alert(1)//url`. Every CP or DP
* check that hands a surface schema to Ajv (WS-28's settings check on platform-api among them)
* hands it this form.
*
* `schema` is untrusted JSON, typically one value from an `app.json` or a stored manifest. When
* {@link isRepresentable} gives `false` for it, throws {@link SurfaceTypeLanguageError} with
* `not-representable` and reads nothing further. Otherwise returns a copy in which each free string
* node's `pattern` name is replaced by that row's source, and every other member is equal to the
* input's. The walk follows `properties`, `items` and `oneOf` only, so a `default` or `description`
* value is copied as it stands even when it holds a key named `pattern`. Every schema node of the
* copy, and each `properties` map and `oneOf` array, is a new frozen object; a `default` value is
* the input's own value, neither copied nor frozen.
*
* Each row source is anchored with `^` and `$`, so JSON Schema's search semantics test the whole
* value. The validator must compile patterns with the `u` flag, as every reader does: Ajv 8's
* default `unicodeRegExp: true` does. With that, the copy and the zod parser built by
* {@link surfaceTypes} accept the same values. Pure; never mutates its input.
*/
function surfaceValidatorSchema(schema) {
	if (!isRepresentable(schema)) throw new SurfaceTypeLanguageError("not-representable", "the schema leaves the surface type language (D8), so it has no validator form; check it with isRepresentable first");
	return withPatternSources(schema);
}
/**
* A frozen copy of a representable node with each `pattern` name replaced by its row source. The
* node is representable, so every `properties` value, `items` and `oneOf` variant is a plain object
* node and every `pattern` is a table name.
*/
function withPatternSources(node) {
	const copy = { ...node };
	if (Object.hasOwn(node, "pattern")) copy["pattern"] = SURFACE_PATTERNS[node["pattern"]];
	if (Object.hasOwn(node, "properties")) {
		const properties = node["properties"];
		copy["properties"] = Object.freeze(Object.fromEntries(Object.keys(properties).map((key) => [key, withPatternSources(properties[key])])));
	}
	if (Object.hasOwn(node, "items")) copy["items"] = withPatternSources(node["items"]);
	if (Object.hasOwn(node, "oneOf")) copy["oneOf"] = Object.freeze(node["oneOf"].map(withPatternSources));
	return Object.freeze(copy);
}
/**
* True when `schema` is a JSON Schema node in the closed type language.
*
* `schema` is untrusted JSON, typically one value from an `app.json`. Every lookup reads own
* keys through `Object.hasOwn`, so an inherited `constructor` or `__proto__` never counts as a
* declared member. The walk is depth-first and bounded by {@link SURFACE_SCHEMA_MAX_DEPTH} and
* {@link SURFACE_SCHEMA_MAX_NODES}; it never throws and never mutates its input.
*
* A node is one of these forms, each a plain object carrying no keyword beyond the ones listed
* plus the annotations `description` (a string) and `default` (any value):
* - unknown: no `type` and no `oneOf`;
* - a free string `{ type: 'string', maxLength, pattern? }`, where `maxLength` is an integer from 0
*   to {@link SURFACE_STRING_MAX_LENGTH} and `pattern` is an own key of {@link SURFACE_PATTERNS}
*   (R-36, R-54); a regular expression, even one equal to a row's source, gives `false`;
* - a string enum `{ type: 'string', enum, maxLength? }` of one or more unique strings, with no
*   `pattern`; a `maxLength`, when present, is a valid bound that every member fits in code points
*   (R-52, E6);
* - `{ type: 'number' | 'boolean' | 'null' }`;
* - `{ type: 'object', properties, required?, additionalProperties: false }`, where `required`
*   lists unique names that are own keys of `properties`;
* - `{ type: 'array', items }`;
* - `{ oneOf }` of two or more object nodes on one discriminator: the first property, in the first
*   variant's key order, that every variant requires as `{ type: 'string', const: <string> }`.
*   The tag carries no `pattern`; a `maxLength` on it is checked as on an enum (R-52). The const
*   values are distinct. `const`
*   appears on that property of each variant and nowhere else. The union's variant count is
*   checked against the node budget, and its depth against the depth cap, before any variant is
*   read (R-42).
*
* The root alone may also carry `$schema`, equal to {@link SURFACE_JSON_SCHEMA_DIALECT}. Any
* other value gives `false`: a non-object, an array, `integer`, a type array, `anyOf`, a string
* with no `maxLength`, `minLength`, `$ref`, `title`, a stray `const`, an over-cap tree.
*/
function isRepresentable(schema) {
	return isRepresentableNode(schema, 1, true, false, { nodes: 0 });
}
const STRING_KEYWORDS = /* @__PURE__ */ new Set([
	"type",
	"enum",
	"const",
	"maxLength",
	"pattern"
]);
const SCALAR_KEYWORDS = /* @__PURE__ */ new Set(["type"]);
const OBJECT_KEYWORDS = /* @__PURE__ */ new Set([
	"type",
	"properties",
	"required",
	"additionalProperties"
]);
const ARRAY_KEYWORDS = /* @__PURE__ */ new Set(["type", "items"]);
const UNION_KEYWORDS = /* @__PURE__ */ new Set(["oneOf"]);
const UNKNOWN_KEYWORDS = /* @__PURE__ */ new Set();
function isPlainObject(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** True when every own key of `node` is in `allowed`, an annotation, or a root `$schema`, and the annotations are well formed. */
function keywordsAllowed(node, allowed, isRoot) {
	for (const key of Object.keys(node)) {
		if (allowed.has(key)) continue;
		if (key === "default") continue;
		if (key === "description") {
			if (typeof node[key] !== "string") return false;
			continue;
		}
		if (key === "$schema" && isRoot && node[key] === "https://json-schema.org/draft/2020-12/schema") continue;
		return false;
	}
	return true;
}
function isRepresentableNode(node, depth, isRoot, constAllowed, budget) {
	if (depth > 16) return false;
	budget.nodes += 1;
	if (budget.nodes > 512) return false;
	if (!isPlainObject(node)) return false;
	if (Object.hasOwn(node, "oneOf")) return keywordsAllowed(node, UNION_KEYWORDS, isRoot) && isRepresentableUnion(node["oneOf"], depth, budget);
	if (!Object.hasOwn(node, "type")) return keywordsAllowed(node, UNKNOWN_KEYWORDS, isRoot);
	switch (node["type"]) {
		case "string": return keywordsAllowed(node, STRING_KEYWORDS, isRoot) && isRepresentableString(node, constAllowed, budget);
		case "number":
		case "boolean":
		case "null": return keywordsAllowed(node, SCALAR_KEYWORDS, isRoot);
		case "object": return keywordsAllowed(node, OBJECT_KEYWORDS, isRoot) && isRepresentableObject(node, depth, null, budget);
		case "array": return keywordsAllowed(node, ARRAY_KEYWORDS, isRoot) && Object.hasOwn(node, "items") && isRepresentableNode(node["items"], depth + 1, false, false, budget);
		default: return false;
	}
}
/**
* True when `value` is an integer `maxLength` from 0 to {@link SURFACE_STRING_MAX_LENGTH}. Shared
* with `firstUnrepresentableNode`; module-internal. Pure.
*/
function isStringBound(value) {
	return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 4096;
}
/**
* True when an enum's or a tag's optional `maxLength` holds: absent, or a valid bound that every
* listed value fits in code points (R-52, E6). A value longer than its own bound would make the
* declaration admit less than it lists.
*/
function listedValuesFitBound(node, values) {
	if (!Object.hasOwn(node, "maxLength")) return true;
	const bound = node["maxLength"];
	return isStringBound(bound) && !values.some((value) => exceedsCodePoints(value, bound));
}
function isRepresentableString(node, constAllowed, budget) {
	const hasEnum = Object.hasOwn(node, "enum");
	const hasPattern = Object.hasOwn(node, "pattern");
	if (Object.hasOwn(node, "const")) {
		const tag = node["const"];
		return constAllowed && !hasEnum && !hasPattern && typeof tag === "string" && listedValuesFitBound(node, [tag]);
	}
	if (!hasEnum) return isStringBound(node["maxLength"]) && (!hasPattern || isSurfacePatternName(node["pattern"]));
	if (hasPattern) return false;
	const members = node["enum"];
	if (!Array.isArray(members) || members.length === 0) return false;
	const seen = /* @__PURE__ */ new Set();
	for (const member of members) {
		budget.nodes += 1;
		if (budget.nodes > 512) return false;
		if (typeof member !== "string" || seen.has(member)) return false;
		seen.add(member);
	}
	return listedValuesFitBound(node, members);
}
/**
* An object node. `discriminator` names the one property whose node may carry `const`; it is
* non-null only for a union variant.
*/
function isRepresentableObject(node, depth, discriminator, budget) {
	if (node["additionalProperties"] !== false) return false;
	const properties = node["properties"];
	if (!isPlainObject(properties)) return false;
	if (Object.hasOwn(node, "required") && !requiredDeclared(node["required"], properties)) return false;
	for (const key of Object.keys(properties)) if (!isRepresentableNode(properties[key], depth + 1, false, key === discriminator, budget)) return false;
	return true;
}
function requiredDeclared(required, properties) {
	if (!Array.isArray(required)) return false;
	const seen = /* @__PURE__ */ new Set();
	for (const name of required) {
		if (typeof name !== "string" || seen.has(name) || !Object.hasOwn(properties, name)) return false;
		seen.add(name);
	}
	return true;
}
/**
* True when `node` is `{ type: 'string', const: <string> }` with no `enum` and no `pattern`. A tag
* may carry `maxLength` (R-52, E6); the property's own node check judges that bound.
*/
function isTagNode(node) {
	return isPlainObject(node) && node["type"] === "string" && typeof node["const"] === "string" && !Object.hasOwn(node, "enum") && !Object.hasOwn(node, "pattern");
}
/** The string members of a variant's `required`, read once into a set; empty when it is not an array (R-42). */
function requiredSet(variant) {
	const required = variant["required"];
	return new Set(Array.isArray(required) ? required : []);
}
function isRepresentableUnion(variants, depth, budget) {
	if (!Array.isArray(variants) || variants.length < 2) return false;
	if (depth + 1 > 16 || budget.nodes + variants.length > 512) return false;
	const objects = [];
	for (const variant of variants) {
		if (!isPlainObject(variant) || variant["type"] !== "object" || !isPlainObject(variant["properties"])) return false;
		objects.push(variant);
	}
	const first = objects[0]["properties"];
	const required = objects.map(requiredSet);
	const discriminator = Object.keys(first).find((name) => objects.every((variant, index) => {
		const properties = variant["properties"];
		return Object.hasOwn(properties, name) && isTagNode(properties[name]) && required[index].has(name);
	}));
	if (discriminator === void 0) return false;
	if (new Set(objects.map((variant) => variant["properties"][discriminator].const)).size !== objects.length) return false;
	for (const variant of objects) {
		budget.nodes += 1;
		if (budget.nodes > 512) return false;
		if (!keywordsAllowed(variant, OBJECT_KEYWORDS, false)) return false;
		if (!isRepresentableObject(variant, depth + 1, discriminator, budget)) return false;
	}
	return true;
}
/**
* True for a check whose effect JSON Schema cannot state. `.refine()`, `.superRefine()` and
* `.check(fn)` carry `check: 'custom'`; `.trim()`, `.toLowerCase()`, `.toUpperCase()`,
* `.normalize()` and `.overwrite(fn)` carry `check: 'overwrite'` (R-55, E5).
*/
function isCustomCheck(check) {
	const kind = check._zod.def.check;
	return kind === "custom" || kind === "overwrite";
}
/**
* The {@link SURFACE_PATTERNS} row a node's checks name: the row whose compiled source equals the
* `RegExp` of the node's one `.regex()` check, when that `RegExp` carries the flags `u` and nothing
* else. The comparison reads both sides through `RegExp.prototype.source`, which escapes `/` as
* `\/`, so the `url` row matches the `RegExp` its own source builds. `undefined` for no regex, two
* or more, another source or other flags (R-54).
*/
function tablePatternName(checks) {
	const regexes = (checks ?? []).filter((check) => check instanceof z.core.$ZodCheckRegex);
	if (regexes.length !== 1) return void 0;
	const pattern = regexes[0]._zod.def.pattern;
	if (pattern.flags !== "u") return void 0;
	return Object.keys(SURFACE_PATTERNS).find((name) => COMPILED_SURFACE_PATTERNS.get(name).source === pattern.source);
}
/**
* True for the zod node types whose JSON Schema export writes `pattern` from the node's own checks
* or format: a string (`.regex()`, `.includes()`, `.startsWith()`, `.endsWith()`, `z.email()` and
* the other string formats) and a template literal. A wrapper such as `.optional()` or `.default()`
* carries its inner node's `pattern` and is judged at that inner node (S1 architecture-10).
*/
function writesOwnPattern(type) {
	return type === "string" || type === "template_literal";
}
/** True for zod's stock `$ZodCheckMaxLength`, and false for the {@link SurfaceCheckMaxCodePoints} form. */
function isUnitCountedMaxLength(check) {
	return check instanceof z.core.$ZodCheckMaxLength && !(check instanceof SurfaceCheckMaxCodePoints);
}
function deepFreeze(value) {
	if (typeof value !== "object" || value === null) return value;
	for (const member of Object.values(value)) deepFreeze(member);
	return Object.freeze(value);
}
//#endregion
export { SURFACE_JSON_SCHEMA_DIALECT, SURFACE_PATTERNS, SURFACE_SCHEMA_MAX_DEPTH, SURFACE_SCHEMA_MAX_NODES, SURFACE_STRING_MAX_LENGTH, SURFACE_TYPE_ANNOTATIONS, SurfaceTypeLanguageError, exceedsCodePoints, isRepresentable, isStringBound, isSurfacePatternName, listedValuesFitBound, matchesSurfacePattern, surfaceJsonSchema, surfaceTypes, surfaceValidatorSchema };

//# sourceMappingURL=surface-type-language.js.map